import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
// Components
import HRDashboard from '@/pages/rh/HRDashboard'
import UsersList from '@/pages/configuracion/UsersList'
import UserDetail from '@/pages/configuracion/UserDetail'
import CreateUser from '@/pages/configuracion/CreateUser'
import AuditHistoryList from '@/pages/rh/AuditHistoryList'
import JobRequestsList from '@/pages/rh/JobRequestsList'
import JobRequestDetail from '@/pages/rh/JobRequestDetail'
import CreateJobRequest from '@/pages/rh/CreateJobRequest'
import JobRequestsRHDashboard from '@/pages/rh/JobRequestsRHDashboard'
import ActiveJobsList from '@/pages/rh/ActiveJobsList'
import { RHDashboard } from '@/pages/rh/RHDashboard'
import NuevoCandidato from '@/pages/rh/NuevoCandidato'
import CierreDeSeleccion from '@/pages/rh/CierreDeSeleccion'
import VistaDeDireccion from '@/pages/rh/VistaDeDireccion'
import { JobRequestsHistory } from '@/pages/rh/JobRequestsHistory'
import HRInterviewsManagement from '@/pages/rh/HRInterviewsManagement'
import { HiringManagerInterviewsView } from '@/pages/rh/HiringManagerInterviewsView'
import CandidateDetailView from '@/pages/rh/CandidateDetailView'
import Login from '@/pages/auth/Login'
import CandidateRegistrationForm from '@/pages/rh/CandidateRegistrationForm'
import AvisoDePrivacidadIntegral from '@/pages/rh/AvisoDePrivacidadIntegral'
import RegisterProviders from '@/pages/proveedores/RegisterProvider'
import RegisterProviderByInvitation from '@/pages/proveedores/RegisterProviderByInvitation'
import LoginProviders from '@/pages/proveedores/LoginProviders'
import LoginAlmacen from '@/pages/almacen/LoginAlmacen'
import SupplierLayout from '@/features/proveedores/components/SupplierLayout'
import AdminPanelWrapper from '@/pages/configuracion/AdminPanelWrapper'
import AuthenticatedLayout from '@/shared/components/AuthenticatedLayout'
import SupplierForm from '@/features/proveedores/components/SupplierForm'
import SuperAdminProveedores from '@/pages/compras/SuperAdminProveedores'
import PaquetesMensualesPendientes from '@/pages/compras/PaquetesMensualesPendientes'
import SolicitudesProveedores from '@/pages/compras/SolicitudesProveedores'
import BorradoresProveedores from '@/pages/compras/BorradoresProveedores'
import SupplierRequestDetail from '@/pages/compras/SupplierRequestDetail'
import AdminSupplierForm from '@/features/proveedores/components/AdminSupplierForm'
import EditSupplierForm from '@/features/proveedores/components/EditSupplierForm'
import CreateSupplierForm from '@/features/proveedores/components/CreateSupplierForm'
import CreateSupplierInvitation from '@/pages/compras/CreateSupplierInvitation'
import PurchaseOrderDetail from '@/pages/compras/PurchaseOrderDetail'
import CrudRoles from '@/pages/configuracion/roles/crudRolesPage'
import { NotificationsPage } from '@/pages/rh/NotificationsPage';
import SupplierNotificationsPage from '@/pages/proveedores/SupplierNotificationsPage';
import { NotificationsProvider } from './context/NotificationsContext';
import GlobalPaymentsView from '@/features/rh/components/rh/GlobalPaymentsView';

// Employee Management Components
import { CandidatesReadyToHire, HireForm } from '@/features/rh/components/hiring'
import { EmployeesList, EmployeeDetail, EmployeeEdit, EmployeeDocuments, EmployeeContracts } from '@/features/rh/components/employees'
import { ContractTemplates, DocumentTypesCatalog } from '@/features/configuracion/components/settings'
import { PurchaseOrdersRH, PurchaseOrderPayments, SupplierEvaluations, EvaluationDetail, CreateEvaluation } from '@/features/rh/components/rh'
import PurchaseOrderDetailPage from './pages/rh/PurchaseOrderDetailPage'
import ModulesView from '@/pages/configuracion/ModulesView'
import ChangePasswordPage from '@/pages/auth/ChangePasswordPage'
import PurchaseOrdersView from '@/pages/compras/PurchaseOrdersView'
import { SupplierPayments } from '@/pages/proveedores/SupplierPayments'
import SupplierDashboard from '@/pages/proveedores/SupplierDashboard'
import ProveedoresDashboard from '@/pages/compras/ProveedoresDashboard'
import AlmacenDashboard from '@/pages/almacen/AlmacenDashboard'
import DashboardLayout from '@/app/layouts/DashboardLayout'
import AsignarPage from '@/pages/almacen/asignacion/AsignarPage'
import ReasignarPage from '@/pages/almacen/asignacion/ReasignarPage'
import DevolverPage from '@/pages/almacen/asignacion/DevolverPage'
import HerramientaPage from '@/pages/almacen/registro/HerramientaPage'
import ProyectosAlmacen from '@/pages/almacen/ProyectosAlmacen'
import HerramientasAlmacen from '@/pages/almacen/HerramientasAlmacen'
import AsignacionHerramientas from '@/pages/almacen/AsignacionHerramientas'
import MantenimientoAlmacen from '@/pages/almacen/MantenimientoAlmacen'
import ReportesActivos from '@/pages/almacen/ReportesActivos'
import HistorialHerramientas from '@/pages/almacen/HistorialHerramientas'
import HistorialResponsable from '@/pages/almacen/HistorialResponsable'
import CreateInvitationsPage from '@/pages/compras/CreateInvitations'
import { ProtectedRoute } from '@/shared/components'
// Admin Components
import { useAuth } from '@/shared/hooks/useAuth'
import { useDebugInfo } from '@/shared/hooks/useDebugInfo'
import { SystemThemeProvider } from './hooks/useSystemTheme'
import ResetPassword from '@/pages/auth/ResetPassword'
import { config } from './config/env'
import { moduloDelUsuario } from '@/shared/lib/moduloDelUsuario'
import { EsqueletoLinea } from '@/shared/components/Esqueleto'

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Dashboard Content Component - Main Dashboard (HRDashboard)
const DashboardContent = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [currentModuleKey, setCurrentModuleKey] = useState<string | null>(null);
  const [moduleLoading, setModuleLoading] = useState(true);

  useEffect(() => {
    const loadCurrentModuleKey = async () => {
      setModuleLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token || !user?.currentModuleId) {
        setCurrentModuleKey(null);
        setModuleLoading(false);
        return;
      }

      try {
        const response = await fetch(`${config.apiBaseUrl}/users/me/modules`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          setCurrentModuleKey(null);
          setModuleLoading(false);
          return;
        }

        const data = await response.json();
        const modules = Array.isArray(data?.modules) ? data.modules : [];
        const activeModule = modules.find((module: { id?: string; key?: string }) => module.id === user.currentModuleId);

        setCurrentModuleKey(activeModule?.key ?? null);
      } catch (error) {
        console.error('Error loading current module key:', error);
        setCurrentModuleKey(null);
      } finally {
        setModuleLoading(false);
      }
    };

    if (!authLoading) {
      loadCurrentModuleKey();
    }
  }, [user?.currentModuleId, authLoading]);

  // Esperar auth + módulo antes de decidir qué dashboard mostrar.
  //
  // A-01: silueta en vez de spinner. Los tres dashboards comparten forma —una
  // fila de indicadores y dos paneles—, así que se puede dibujar sin saber cuál
  // toca. Antes era un spinner centrado en un `flex-1` que no se estira, así
  // que acababa pegado al encabezado en lugar de al centro.
  if (authLoading || moduleLoading) {
    return (
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4"
            >
              <EsqueletoLinea className="w-2/3" />
              <EsqueletoLinea className="w-1/3 h-8" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4"
            >
              <EsqueletoLinea className="w-40 h-4" />
              <EsqueletoLinea className="w-full" />
              <EsqueletoLinea className="w-5/6" />
              <EsqueletoLinea className="w-3/4" />
            </div>
          ))}
        </div>
      </main>
    );
  }

  // La misma regla que usa la campanita para saber qué notificaciones mostrar.
  // Vivía suelta aquí y las dos se salían: en una cuenta sin módulo activo
  // configurado este panel mostraba Almacén y la campanita filtraba por RH.
  const moduloActivo = moduloDelUsuario({
    claveDelModuloActivo: currentModuleKey,
    esMaster: user?.rbac?.isMaster,
    permisos: user?.rbac?.effectivePermissions,
  });

  if (moduloActivo === "suppliers") {
    return (
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
        <ProveedoresDashboard />
      </main>
    );
  } else if (moduloActivo === "almacen") {
    return (
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
        <AlmacenDashboard />
      </main>
    );
  } else if (user) {
    return (
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
        <HRDashboard />
      </main>
    );
  }
};

// ── Page wrapper components (read params, pass as props) ───────────────────

/** Lleva /registro/:token a la pantalla de registro, que espera ?token=. */
const RedirigirInvitacion = () => {
  const { token } = useParams<{ token: string }>()
  return (
    <Navigate
      to={`/proveedores/registro-invitacion?token=${encodeURIComponent(token ?? '')}`}
      replace
    />
  )
}

const SupplierDetailPageWrapper = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  if (!id) return null
  return (
    <SupplierRequestDetail
      supplierId={id}
      onBack={() => navigate(-1 as any)}
      onEdit={() => navigate(`/super-admin-proveedores/${id}/editar`)}
    />
  )
}

const SupplierEditPageWrapper = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  if (!id) return null
  return (
    <AdminSupplierForm
      supplierId={id}
      onBack={() => navigate(-1 as any)}
      onSuccess={() => navigate(`/super-admin-proveedores/${id}`)}
    />
  )
}

const CreateSupplierPage = () => {
  const navigate = useNavigate()
  return (
    <CreateSupplierForm
      onBack={() => navigate('/super-admin-proveedores')}
      onSuccess={() => navigate('/super-admin-proveedores')}
    />
  )
}

const CreateInvitationPage = () => {
  const navigate = useNavigate()
  return (
    <CreateSupplierInvitation
      onBack={() => navigate('/super-admin-proveedores')}
      onSuccess={() => navigate('/super-admin-proveedores')}
    />
  )
}

const SolicitudDetailPageWrapper = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  if (!id) return null
  return (
    <SupplierRequestDetail
      supplierId={id}
      onBack={() => navigate(-1 as any)}
      onEdit={() => navigate(`/solicitudes-proveedores/${id}/editar`)}
    />
  )
}

const SolicitudEditPageWrapper = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  if (!id) return null
  return (
    <EditSupplierForm
      supplierId={id}
      onBack={() => navigate(-1 as any)}
      onSuccess={() => navigate(`/solicitudes-proveedores/${id}`)}
    />
  )
}

const BorradorDetailPageWrapper = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  if (!id) return null
  return (
    <AdminSupplierForm
      supplierId={id}
      onBack={() => navigate('/borradores')}
      onSuccess={() => navigate('/borradores')}
    />
  )
}

const PurchaseOrderDetailWrapper = () => {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  if (!orderId) return null
  return (
    <PurchaseOrderDetail
      orderId={orderId}
      onBack={() => navigate(-1 as any)}
    />
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function App() {
  const { isAuthenticated, user, isLoading, initializing, error, login, logout } = useAuth()
  const [isSupplierAuthenticated, setIsSupplierAuthenticated] = useState(Boolean(localStorage.getItem('supplier_access_token')))

  // Debug info in development
  useDebugInfo()

  if (initializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <SystemThemeProvider enabled={Boolean(isAuthenticated)}>
        <Toaster position="bottom-right" />
        <NotificationsProvider>
          <Router>
            <Routes>
              {/* Ruta pública para el registro de candidatos */}
              <Route
                path="/candidato/registro"
                element={<CandidateRegistrationForm />}
              />
              {/* Aviso de privacidad integral: público, se abre desde el formulario */}
              <Route path="/aviso-de-privacidad" element={<AvisoDePrivacidadIntegral />} />
              {/* Ruta para el formulario de proveedor (completar expediente) */}
              <Route
                path="/proveedor/formulario"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="proveedor-perfil" user={user} onLogout={logout}>
                      <SupplierForm onBack={() => window.history.back()} onSupplierCreated={() => {}} />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              {/* Ruta pública para el login de proveedores */}
              <Route
                path="/proveedores/login"
                element={<LoginProviders onLogin={async () => { setIsSupplierAuthenticated(true) }} isLoading={false} error={null} />}
              />
              {/* Rutas autenticadas del módulo Almacén */}
              {/* Almacén: rutas reales, cada una con su pantalla. Antes las siete
                  apuntaban al mismo componente, que volvía a decidir con un
                  switch, y cada pantalla parseaba la URL otra vez con regex. */}
              <Route
                path="/almacen"
                element={
                  isAuthenticated ? (
                    <DashboardLayout user={user} onLogout={logout} />
                  ) : (
                    <Navigate to="/almacen/login" replace />
                  )
                }
              >
                <Route path="registro" element={<HerramientasAlmacen />} />
                <Route path="registro/crear" element={<HerramientasAlmacen />} />
                {/* El detalle de requisicion tiene su propia URL; no choca con
                    registro/:id porque lleva un segmento mas. */}
                <Route path="registro/requisicion/:requisicionId" element={<HerramientasAlmacen />} />
                <Route path="registro/:id" element={<HerramientaPage mode="view" />} />
                <Route path="registro/:id/editar" element={<HerramientaPage mode="edit" />} />

                <Route path="asignacion" element={<AsignacionHerramientas key="asignacion" />} />
                <Route path="asignacion/asignar" element={<AsignarPage />} />
                <Route
                  path="asignacion/:id/ver"
                  element={<HerramientaPage mode="view" volverA="/almacen/asignacion" />}
                />
                <Route path="asignacion/:id/reasignar" element={<ReasignarPage />} />
                <Route path="asignacion/:id/devolver" element={<DevolverPage />} />

                <Route path="devolucion" element={<AsignacionHerramientas key="devolucion" vista="devolucion" />} />
                <Route path="devolucion/devolver" element={<DevolverPage generica />} />
                {/* Las mismas acciones que en asignación, pero regresando a
                    Devolución: si no, el menú lateral se marca en el módulo
                    equivocado y el botón de volver saca al usuario de aquí. */}
                <Route
                  path="devolucion/:id/ver"
                  element={<HerramientaPage mode="view" volverA="/almacen/devolucion" />}
                />
                <Route
                  path="devolucion/:id/reasignar"
                  element={<ReasignarPage volverA="/almacen/devolucion" />}
                />
                <Route
                  path="devolucion/:id/devolver"
                  element={<DevolverPage volverA="/almacen/devolucion" />}
                />

                <Route path="mantenimiento" element={<MantenimientoAlmacen />} />
                <Route path="mantenimiento/:id" element={<MantenimientoAlmacen />} />

                <Route path="reportes" element={<ReportesActivos />} />
                <Route path="reportes/nuevo" element={<ReportesActivos />} />
                <Route path="reportes/:id/ver" element={<ReportesActivos />} />

                <Route path="proyectos" element={<ProyectosAlmacen />} />
                <Route path="historial/herramientas" element={<HistorialHerramientas />} />
                {/* El historial de una herramienta tiene su propia URL: antes vivia
                    en la misma que el listado, con la seleccion en estado, asi que
                    no se podia compartir, recargar ni volver con el navegador. */}
                <Route path="historial/herramientas/:toolId" element={<HistorialHerramientas />} />
                <Route path="historial/responsable" element={<HistorialResponsable />} />
                <Route path="historial/responsable/:responsableId" element={<HistorialResponsable />} />
              </Route>

              {/* Ruta pública para el login de almacén */}
              <Route
                path="/almacen/login"
                element={
                  isAuthenticated ? (
                    <Navigate to="/almacen/dashboard" replace />
                  ) : (
                    <LoginAlmacen onLogin={login} isLoading={isLoading} error={error} />
                  )
                }
              />
              {/* Ruta pública para el registro de proveedores (sin invitación) */}
              <Route
                path="/proveedores/registro"
                element={<RegisterProviders onLogin={async () => { }} isLoading={false} error={null} />}
              />
              {/* Ruta pública para el registro de proveedores con invitación (con token) */}
              <Route
                path="/proveedores/registro-invitacion"
                element={<RegisterProviderByInvitation />}
              />
              {/* El correo de invitación manda a /registro/:token, que no era una
                  ruta del portal: caía en el comodín y terminaba en el login. Se
                  aceptan las dos formas y se reenvían a la pantalla real, para que
                  los enlaces ya enviados sigan sirviendo. */}
              <Route path="/registro/:token" element={<RedirigirInvitacion />} />
              <Route
                path="/proveedores/registro/:token"
                element={<RedirigirInvitacion />}
              />
              {/* Ruta para el dashboard de proveedores */}
              <Route
                path="/proveedores/dashboard"
                element={
                  isSupplierAuthenticated ? (
                    <SupplierLayout activeRoute="dashboard">
                      <SupplierDashboard />
                    </SupplierLayout>
                  ) : (
                    <Navigate to="/proveedores/login" replace />
                  )
                }
              />
              <Route
                path="/proveedores/notificaciones"
                element={
                  isSupplierAuthenticated ? (
                    <SupplierLayout activeRoute="notificaciones">
                      <SupplierNotificationsPage />
                    </SupplierLayout>
                  ) : (
                    <Navigate to="/proveedores/login" replace />
                  )
                }
              />
              {/* Ruta para pagos de OC */}
              <Route
                path="/pagos"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="pagos" user={user} onLogout={logout}>
                      <GlobalPaymentsView />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              {/* Ruta para el expediente de proveedores */}
              <Route
                path="/proveedores/invitacion"
                element={
                  isSupplierAuthenticated ? (
                    <SupplierLayout>
                      <CreateInvitationsPage />
                    </SupplierLayout>
                  ) : (
                    <Navigate to="/proveedores/login" replace />
                  )
                }
              />
              {/* Ruta para el expediente de proveedores */}
              <Route
                path="/proveedores/expediente"
                element={
                  isSupplierAuthenticated ? (
                    <SupplierLayout activeRoute="expediente">
                      <SupplierForm onBack={() => window.history.back()} onSupplierCreated={() => {}} />
                    </SupplierLayout>
                  ) : (
                    <Navigate to="/proveedores/login" replace />
                  )
                }
              />
              {/* Ruta para ver detalle de candidato */}
              <Route
                path="/candidate/:candidateId"
                element={
                  isAuthenticated ? (
                    <CandidateDetailView />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              {/* */}
              <Route
                path="/roles/:crudprop"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="roles" user={user} onLogout={logout}>
                      <CrudRoles />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/cambiar-contrasena"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="cambiar-contrasena" user={user} onLogout={logout}>
                      <ChangePasswordPage />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/login"
                element={
                  isAuthenticated ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <Login
                      onLogin={login}
                      isLoading={isLoading}
                      error={error}
                    />
                  )
                }
              />
              <Route
                path="/login/:token"
                element={
                  <ResetPassword onLogin={login} isLoading={isLoading} error={error} />
                }
              />
              {/* Ruta alternativa para /auth/login - redirige a /login */}
              <Route
                path="/auth/login"
                element={<Navigate to="/login" replace />}
              />
              {/* Pantallas con barra lateral. Antes las siete apuntaban al
                  componente Dashboard, que guardaba la pantalla activa en un
                  useState, la sincronizaba con la URL en un useEffect y elegia
                  con un switch. El marco ahora es un layout con Outlet y cada
                  ruta declara lo suyo. */}
              <Route
                element={
                  isAuthenticated ? (
                    <DashboardLayout user={user} onLogout={logout} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              >
                <Route path="/dashboard" element={<DashboardContent />} />
                <Route path="/rh-dashboard" element={<RHDashboard />} />
                <Route path="/rh/candidatos/nuevo" element={<NuevoCandidato />} />
                <Route path="/rh/cierre-seleccion" element={<CierreDeSeleccion />} />
                <Route path="/rh/validacion-direccion" element={<VistaDeDireccion />} />
                <Route path="/job-requests-history" element={<JobRequestsHistory />} />
                <Route path="/proveedores" element={<SupplierDashboard />} />
                {/* Estas traian su propio contenedor con margenes dentro del switch */}
                <Route
                  path="/hr-interviews"
                  element={
                    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
                      <HRInterviewsManagement />
                    </div>
                  }
                />
                <Route
                  path="/active-jobs"
                  element={
                    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
                      <ActiveJobsList />
                    </div>
                  }
                />
                <Route
                  path="/modules"
                  element={
                    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
                      <ModulesView />
                    </div>
                  }
                />
              </Route>

              {/* Ruta alternativa para /auth/* - redirige a /login */}
              <Route
                path="/auth/*"
                element={<Navigate to="/login" replace />}
              />

              {/* Users routes */}
              <Route
                path="/users"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="users" user={user} onLogout={logout}>
                      <UsersList />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/users/new"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="users" user={user} onLogout={logout}>
                      <CreateUser />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/users/audit-history"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="users" user={user} onLogout={logout}>
                      <AuditHistoryList />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/users/:id"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="users" user={user} onLogout={logout}>
                      <UserDetail />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              {/* La edición tiene su propia URL para que el botón Atrás del
                  navegador regrese a la consulta y no al listado. */}
              <Route
                path="/users/:id/editar"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="users" user={user} onLogout={logout}>
                      <UserDetail />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              {/* Job Requests routes */}
              <Route
                path="/job-requests"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="job-requests" user={user} onLogout={logout}>
                      <JobRequestsList />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/job-requests/new"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="job-requests" user={user} onLogout={logout}>
                      <CreateJobRequest />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/job-requests/:id"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="job-requests" user={user} onLogout={logout}>
                      <JobRequestDetail />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/job-requests-rh"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="job-requests-rh" user={user} onLogout={logout}>
                      <JobRequestsRHDashboard />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/job-requests-rh/:id"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="job-requests-rh" user={user} onLogout={logout}>
                      <JobRequestDetail />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/hiring-manager-interviews"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="hiring-manager-interviews" user={user} onLogout={logout}>
                      <HiringManagerInterviewsView />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/purchase-orders"
                element={
                  isAuthenticated ? (
                    <PurchaseOrdersView />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              {/* Ruta para órdenes de compra de proveedores */}
              <Route
                path="/proveedores/ordenes"
                element={
                  isSupplierAuthenticated ? (
                    <SupplierLayout activeRoute="ordenes">
                      <PurchaseOrdersView />
                    </SupplierLayout>
                  ) : (
                    <Navigate to="/proveedores/login" replace />
                  )
                }
              />
              {/* Ruta para detalle de una orden específica de proveedores */}
              <Route
                path="/proveedores/ordenes/:orderId"
                element={
                  isSupplierAuthenticated ? (
                    <SupplierLayout activeRoute="ordenes">
                      <PurchaseOrderDetailWrapper />
                    </SupplierLayout>
                  ) : (
                    <Navigate to="/proveedores/login" replace />
                  )
                }
              />
              {/* Ruta para pagos de una orden específica de proveedores */}
              <Route
                path="/proveedores/ordenes/:orderId/pagos"
                element={
                  isSupplierAuthenticated ? (
                    <SupplierLayout activeRoute="ordenes">
                      <SupplierPayments />
                    </SupplierLayout>
                  ) : (
                    <Navigate to="/proveedores/login" replace />
                  )
                }
              />
              <Route
                path="/admin"
                element={
                  isAuthenticated ? (
                    <Navigate to="/admin/projects" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/super-admin-proveedores"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="super-admin-proveedores" user={user} onLogout={logout}>
                      <SuperAdminProveedores />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/super-admin-proveedores/nuevo"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="super-admin-proveedores" user={user} onLogout={logout}>
                      <CreateSupplierPage />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/super-admin-proveedores/invitacion/nueva"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="super-admin-proveedores" user={user} onLogout={logout}>
                      <CreateInvitationPage />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/super-admin-proveedores/paquetes-mensuales"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="super-admin-proveedores" user={user} onLogout={logout}>
                      <PaquetesMensualesPendientes />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/super-admin-proveedores/:id/editar"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="super-admin-proveedores" user={user} onLogout={logout}>
                      <SupplierEditPageWrapper />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/super-admin-proveedores/:id"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="super-admin-proveedores" user={user} onLogout={logout}>
                      <SupplierDetailPageWrapper />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/solicitudes-proveedores"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="solicitudes-proveedores" user={user} onLogout={logout}>
                      <SolicitudesProveedores />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/solicitudes-proveedores/:id/editar"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="solicitudes-proveedores" user={user} onLogout={logout}>
                      <SolicitudEditPageWrapper />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/solicitudes-proveedores/:id"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="solicitudes-proveedores" user={user} onLogout={logout}>
                      <SolicitudDetailPageWrapper />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/borradores"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="borradores" user={user} onLogout={logout}>
                      <BorradoresProveedores />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/borradores/:id"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="borradores" user={user} onLogout={logout}>
                      <BorradorDetailPageWrapper />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/admin/*"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="admin" user={user} onLogout={logout}>
                      <AdminPanelWrapper />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* Employee Management Routes */}
              <Route
                path="/hiring/candidates"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="hiring/candidates" user={user} onLogout={logout}>
                      <CandidatesReadyToHire />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/hiring/new"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="employees" user={user} onLogout={logout}>
                      <HireForm />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/hiring/hire/:candidateId"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="hiring" user={user} onLogout={logout}>
                      <HireForm />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/employees"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout activeRoute="employees" user={user} onLogout={logout}>
                      <EmployeesList />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/:id"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="employees" user={user} onLogout={logout}>
                      <EmployeeDetail />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/employees/:id/edit"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="employees" user={user} onLogout={logout}>
                      <EmployeeEdit />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/employees/:id/documents"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="employees" user={user} onLogout={logout}>
                      <EmployeeDocuments />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/employees/:id/contracts"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="employees" user={user} onLogout={logout}>
                      <EmployeeContracts />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/rh/purchase-orders"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="rh/purchase-orders" user={user} onLogout={logout}>
                      <PurchaseOrdersRH />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/rh/purchase-orders/:orderId"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="rh/purchase-orders" user={user} onLogout={logout}>
                      <PurchaseOrderDetailPage />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/rh/purchase-orders/:orderId/payments"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="rh/purchase-orders" user={user} onLogout={logout}>
                      <PurchaseOrderPayments />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/rh/evaluations"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="rh/evaluations" user={user} onLogout={logout}>
                      <SupplierEvaluations />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/rh/evaluations/new"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="rh/evaluations" user={user} onLogout={logout}>
                      <CreateEvaluation />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/rh/evaluations/:evaluationId"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="rh/evaluations" user={user} onLogout={logout}>
                      <EvaluationDetail />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/notifications"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="notifications" user={user} onLogout={logout}>
                      <NotificationsPage />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* Settings Routes */}
              <Route
                path="/settings/contract-templates"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="settings" user={user} onLogout={logout}>
                      <ContractTemplates />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/settings/document-types"
                element={
                  isAuthenticated ? (
                    <AuthenticatedLayout activeRoute="settings" user={user} onLogout={logout}>
                      <DocumentTypesCatalog />
                    </AuthenticatedLayout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* Menu Management Route - handled by /admin/* wildcard */}

              <Route path="/" element={<Navigate to="/login" replace />} />
              {/* Catch-all route - redirige rutas no encontradas */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Router>
        </NotificationsProvider>
      </SystemThemeProvider>
    </QueryClientProvider>
  )
}

export default App
